import {
	BankOutlined,
	CreditCardOutlined,
	DownloadOutlined,
	LeftOutlined,
	ReloadOutlined,
	SearchOutlined,
	WalletOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, DatePicker, Input, Pagination, Select, Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { buildingsApi } from "@/modules/objects/api/buildings.api";
import { getPayments } from "@/modules/payments/services/payments.api";
import type { PaymentMethod, PaymentRecord } from "@/modules/payments/types/payments.types";
import { formatUzs, paymentMethodLabels } from "@/modules/payments/utils/payments.utils";

const { RangePicker } = DatePicker;

type PaymentMethodFilter = PaymentMethod | "all";

type MethodSummary = {
	method: PaymentMethod;
	total: number;
	count: number;
	color: string;
};

const methodColors: Record<PaymentMethod, string> = {
	naqd: "#2474d6",
	bank: "#2756d8",
	karta: "#7a4dd6",
};

// ─── Types & Helpers ──────────────────────────────────────────────────────────

function getMonthLabel(date: string) {
	const labels = [
		"Yan",
		"Fev",
		"Mar",
		"Apr",
		"May",
		"Iyun",
		"Iyul",
		"Avg",
		"Sen",
		"Okt",
		"Noy",
		"Dek",
	];
	return labels[new Date(date).getMonth()] ?? "";
}

function initials(name: string) {
	return name
		.split(" ")
		.map((part) => part[0])
		.join("")
		.slice(0, 2);
}

function buildPaymentReport(payments: PaymentRecord[]) {
	const total = payments.reduce((sum, item) => sum + item.amountUzs, 0);
	const totalDiscount = payments.reduce((sum, item) => sum + item.discountAmountUzs, 0);

	const byMethod = (["naqd", "bank", "karta"] as PaymentMethod[]).map<MethodSummary>((method) => {
		const methodPayments = payments.filter((payment) => payment.paymentMethod === method);
		return {
			method,
			total: methodPayments.reduce((sum, payment) => sum + payment.amountUzs, 0),
			count: methodPayments.length,
			color: methodColors[method],
		};
	});

	const monthMap = payments.reduce((map, payment) => {
		const label = getMonthLabel(payment.paymentDate);
		const current = map.get(label) ?? { label, total: 0, count: 0 };
		current.total += payment.amountUzs;
		current.count += 1;
		map.set(label, current);
		return map;
	}, new Map<string, { label: string; total: number; count: number }>());

	const monthly = ["Yan", "Fev", "Mar", "Apr", "May", "Iyun"].map(
		(label) => monthMap.get(label) ?? { label, total: 0, count: 0 }
	);

	return {
		averageCheck: payments.length > 0 ? total / payments.length : 0,
		byMethod,
		monthly,
		total,
		totalCount: payments.length,
		totalDiscount,
	};
}

function CashLineChart({
	data,
	onPointClick,
}: {
	data: { label: string; total: number; count: number }[];
	onPointClick?: (label: string) => void;
}) {
	const max = Math.max(...data.map((item) => item.total), 1) * 1.15;
	const x = (index: number) => 26 + index * ((520 - 52) / Math.max(data.length - 1, 1));
	const y = (value: number) => 166 - (value / max) * 138;
	const points = data.map((item, index) => `${x(index)},${y(item.total)}`).join(" ");
	const areaPoints = `${x(0)},166 ${points} ${x(data.length - 1)},166`;

	return (
		<svg
			className="crm-cash-line-chart"
			viewBox="0 0 520 190"
			role="img"
			aria-label="Oylik kassa dinamikasi"
		>
			{[0, 1, 2, 3].map((line) => {
				const gridY = 28 + line * ((166 - 28) / 3);
				return <line key={line} x1="26" x2="494" y1={gridY} y2={gridY} />;
			})}
			<polygon points={areaPoints} />
			<polyline points={points} />
			{data.map((item, index) => (
				<Tooltip key={item.label} title={`${formatUzs(item.total)}`}>
					<g
						style={{ cursor: onPointClick ? "pointer" : "default" }}
						onClick={() => onPointClick?.(item.label)}
					>
						<circle
							cx={x(index)}
							cy={y(item.total)}
							r="6"
							style={{ transition: "r 0.2s" }}
							onMouseEnter={(e) => e.currentTarget.setAttribute("r", "8")}
							onMouseLeave={(e) => e.currentTarget.setAttribute("r", "6")}
						/>
						<text textAnchor="middle" x={x(index)} y="184">
							{item.label}
						</text>
					</g>
				</Tooltip>
			))}
		</svg>
	);
}

function MethodDonut({
	items,
	total,
	onSegmentClick,
}: {
	items: MethodSummary[];
	total: number;
	onSegmentClick?: (method: PaymentMethod) => void;
}) {
	const circumference = 2 * Math.PI * 58;
	let offset = 0;

	return (
		<div className="crm-cash-donut-wrap">
			<div className="crm-donut crm-cash-donut">
				<svg aria-hidden="true" viewBox="0 0 140 140">
					<circle cx="70" cy="70" r="58" />
					{items.map((item) => {
						const dash = total > 0 ? (item.total / total) * circumference : 0;
						const strokeDashoffset = -offset;
						offset += dash;

						return (
							<Tooltip
								key={item.method}
								title={`${paymentMethodLabels[item.method]}: ${formatUzs(item.total)}`}
							>
								<circle
									cx="70"
									cy="70"
									r="58"
									onClick={() => onSegmentClick?.(item.method)}
									style={{
										stroke: item.color,
										strokeDasharray: `${dash} ${circumference}`,
										strokeDashoffset,
										cursor: onSegmentClick ? "pointer" : "default",
										transition: "opacity 0.2s",
									}}
									onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
									onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
								/>
							</Tooltip>
						);
					})}
				</svg>
				<div>
					<strong>{formatUzs(total).replace(" UZS", "")}</strong>
					<span>UZS</span>
				</div>
			</div>
			<div className="crm-cash-method-list">
				{items.map((item) => {
					const percent = total > 0 ? Math.round((item.total / total) * 100) : 0;
					return (
						<div key={item.method}>
							<i style={{ background: item.color }} />
							<span>{paymentMethodLabels[item.method]}</span>
							<strong>{percent}%</strong>
							<small>{item.count} ta</small>
						</div>
					);
				})}
			</div>
		</div>
	);
}

export function PaymentReportPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const [method, setMethod] = useState<PaymentMethodFilter>("all");
	const [building, setBuilding] = useState("all");
	const [searchQuery, setSearchQuery] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [page, setPage] = useState(1);
	const pageSize = 20;
	const [dateRange, setDateRange] = useState<[string, string] | null>(() => {
		const state = location.state as any;
		if (state?.thisMonth) {
			return [
				dayjs().startOf("month").format("YYYY-MM-DD"),
				dayjs().endOf("month").format("YYYY-MM-DD"),
			];
		}
		if (state?.filter) {
			const labels = [
				"Yan",
				"Fev",
				"Mar",
				"Apr",
				"May",
				"Iyun",
				"Iyul",
				"Avg",
				"Sen",
				"Okt",
				"Noy",
				"Dek",
			];
			const monthIndex = labels.indexOf(state.filter);
			if (monthIndex !== -1) {
				const start = dayjs().month(monthIndex).startOf("month").format("YYYY-MM-DD");
				const end = dayjs().month(monthIndex).endOf("month").format("YYYY-MM-DD");
				return [start, end];
			}

			if (state.filter.includes("hafta")) {
				const weekNum = parseInt(state.filter.split("-")[0], 10);
				if (!Number.isNaN(weekNum)) {
					const start = dayjs()
						.startOf("month")
						.add(weekNum - 1, "week")
						.startOf("week")
						.format("YYYY-MM-DD");
					const end = dayjs()
						.startOf("month")
						.add(weekNum - 1, "week")
						.endOf("week")
						.format("YYYY-MM-DD");
					return [start, end];
				}
			}
		}
		return null;
	});

	const fromDashboard = (location.state as any)?.fromDashboard === true;

	const { data: buildings = [] } = useQuery({
		queryKey: ["buildings", "list"],
		queryFn: () => buildingsApi.getBuildings(),
		staleTime: 5 * 60 * 1000,
	});

	// Reset page to 1 when filters change
	useEffect(() => {
		if (method || building || dateRange || searchQuery) {
			setPage(1);
		}
	}, [method, building, dateRange, searchQuery]);

	const { data: paymentsResponse, isLoading } = useQuery({
		queryKey: ["payments", "report", page, pageSize, dateRange, method],
		queryFn: () =>
			getPayments({
				limit: pageSize,
				page,
				...(dateRange ? { dateFrom: dateRange[0], dateTo: dateRange[1] } : {}),
				...(method === "all" ? {} : { method }),
			}),
	});

	const { data: statsResponse } = useQuery({
		queryKey: ["payments", "report-stats", dateRange, method],
		queryFn: () =>
			getPayments({
				limit: 100,
				page: 1,
				...(dateRange ? { dateFrom: dateRange[0], dateTo: dateRange[1] } : {}),
				...(method === "all" ? {} : { method }),
			}),
	});

	const reportPayments = paymentsResponse?.items ?? [];
	const totalPayments = paymentsResponse?.meta?.total ?? 0;
	const statsPayments = statsResponse?.items ?? [];

	const buildingOptions = useMemo(() => {
		return buildings.map((b) => b.name);
	}, [buildings]);

	const filteredPayments = useMemo(() => {
		const normalizedQuery = searchQuery.trim().toLowerCase();

		return reportPayments.filter((payment) => {
			const matchesBuilding = building === "all" || payment.buildingName === building;
			const matchesSearch =
				normalizedQuery.length === 0 ||
				payment.clientName.toLowerCase().includes(normalizedQuery) ||
				payment.receiptNumber.toLowerCase().includes(normalizedQuery) ||
				payment.roomNumber.includes(normalizedQuery) ||
				payment.saleNumber.includes(normalizedQuery);

			return matchesBuilding && matchesSearch;
		});
	}, [building, reportPayments, searchQuery]);

	const filteredStatsPayments = useMemo(() => {
		const normalizedQuery = searchQuery.trim().toLowerCase();

		return statsPayments.filter((payment) => {
			const matchesBuilding = building === "all" || payment.buildingName === building;
			const matchesSearch =
				normalizedQuery.length === 0 ||
				payment.clientName.toLowerCase().includes(normalizedQuery) ||
				payment.receiptNumber.toLowerCase().includes(normalizedQuery) ||
				payment.roomNumber.includes(normalizedQuery) ||
				payment.saleNumber.includes(normalizedQuery);

			return matchesBuilding && matchesSearch;
		});
	}, [building, statsPayments, searchQuery]);

	const report = useMemo(() => buildPaymentReport(filteredStatsPayments), [filteredStatsPayments]);
	const methodMap = Object.fromEntries(
		report.byMethod.map((item) => [item.method, item.total])
	) as Record<PaymentMethod, number>;

	const sortedPayments = useMemo(
		() => [...filteredPayments].sort((a, b) => b.paymentDate.localeCompare(a.paymentDate)),
		[filteredPayments]
	);

	const columns: ColumnsType<PaymentRecord> = [
		{
			title: "Sana",
			dataIndex: "paymentDate",
			key: "paymentDate",
			sorter: (a, b) => a.paymentDate.localeCompare(b.paymentDate),
		},
		{
			title: "Mijoz",
			key: "client",
			render: (_, record) => (
				<div className="crm-org-name-cell">
					<span className="crm-mini-avatar green">{initials(record.clientName)}</span>
					<div>
						<strong className="hover-link" style={{ color: "var(--crm-primary)" }}>
							{record.clientName}
						</strong>
						<span>Shartnoma #{record.saleNumber}</span>
					</div>
				</div>
			),
		},
		{
			title: "Xona",
			key: "room",
			render: (_, record) => (
				<>
					<strong>{record.roomNumber}-xona</strong>
					<span className="crm-table-sub">{record.buildingName}</span>
				</>
			),
		},
		{
			title: "Summa",
			dataIndex: "amountUzs",
			key: "amountUzs",
			align: "right",
			render: (val: number) => <span className="num">{formatUzs(val)}</span>,
			sorter: (a, b) => a.amountUzs - b.amountUzs,
		},
		{
			title: "Usul",
			dataIndex: "paymentMethod",
			key: "paymentMethod",
			render: (method: PaymentMethod) => {
				const cls = method === "naqd" ? "sky" : method === "bank" ? "blue" : "violet";
				return <span className={`crm-badge ${cls}`}>{paymentMethodLabels[method]}</span>;
			},
		},
		{
			title: "Chek",
			dataIndex: "receiptNumber",
			key: "receiptNumber",
		},
		{
			title: "Qabul qildi",
			dataIndex: "receivedBy",
			key: "receivedBy",
		},
	];

	if (isLoading) {
		return (
			<div style={{ padding: 100, textAlign: "center" }}>
				<div style={{ fontSize: 14, color: "var(--crm-ink-3)" }}>Yuklanmoqda...</div>
			</div>
		);
	}

	return (
		<div className="crm-page">
			<div className="crm-page-head">
				<div>
					<div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
						{fromDashboard && (
							<Button icon={<LeftOutlined />} onClick={() => navigate("/dashboard")} type="text" />
						)}
						<h1>To'lov / kassa hisoboti</h1>
					</div>
					<p>Kassa tahlili va barcha to'lovlar reyestri</p>
				</div>
				<div className="crm-report-actions">
					<RangePicker
						className="crm-report-range"
						value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
						onChange={(_, value) =>
							setDateRange(value[0] && value[1] ? [value[0], value[1]] : null)
						}
					/>
					<Select
						options={[
							{ label: "Barcha bino", value: "all" },
							...buildingOptions.map((item) => ({ label: item, value: item })),
						]}
						value={building}
						onChange={setBuilding}
					/>
					<Select
						options={[
							{ label: "Usul: barchasi", value: "all" },
							{ label: "Naqd", value: "naqd" },
							{ label: "Bank", value: "bank" },
							{ label: "Karta", value: "karta" },
						]}
						value={method}
						onChange={setMethod}
					/>
					<Button icon={<DownloadOutlined />}>Eksport</Button>
				</div>
			</div>

			<section className="crm-report-kpis">
				<div
					className="crm-kpi crm-report-kpi"
					style={{
						cursor: "pointer",
						borderColor: method === "all" ? "var(--crm-primary)" : "transparent",
					}}
					onClick={() => setMethod("all")}
				>
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon green">
							<WalletOutlined />
						</span>
						Jami qabul
					</div>
					<div className="crm-kpi-value">
						<strong>{formatUzs(report.total)}</strong>
					</div>
					<div className="crm-kpi-note">{report.totalCount} ta kassa operatsiyasi</div>
				</div>

				<div
					className="crm-kpi crm-report-kpi"
					style={{
						cursor: "pointer",
						borderColor: method === "naqd" ? "var(--crm-primary)" : "transparent",
					}}
					onClick={() => setMethod(method === "naqd" ? "all" : "naqd")}
				>
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon sky">
							<WalletOutlined />
						</span>
						Naqd
					</div>
					<div className="crm-kpi-value">
						<strong>{formatUzs(methodMap.naqd)}</strong>
					</div>
					<div className="crm-kpi-note">kassaga kirim</div>
				</div>

				<div
					className="crm-kpi crm-report-kpi"
					style={{
						cursor: "pointer",
						borderColor: method === "bank" ? "var(--crm-primary)" : "transparent",
					}}
					onClick={() => setMethod(method === "bank" ? "all" : "bank")}
				>
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon blue">
							<BankOutlined />
						</span>
						Bank
					</div>
					<div className="crm-kpi-value">
						<strong>{formatUzs(methodMap.bank)}</strong>
					</div>
					<div className="crm-kpi-note">hisob raqam orqali</div>
				</div>

				<div
					className="crm-kpi crm-report-kpi"
					style={{
						cursor: "pointer",
						borderColor: method === "karta" ? "var(--crm-primary)" : "transparent",
					}}
					onClick={() => setMethod(method === "karta" ? "all" : "karta")}
				>
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon violet">
							<CreditCardOutlined />
						</span>
						Karta
					</div>
					<div className="crm-kpi-value">
						<strong>{formatUzs(methodMap.karta)}</strong>
					</div>
					<div className="crm-kpi-note">terminal va karta</div>
				</div>
			</section>

			<section className="crm-report-grid crm-cash-report-grid">
				<div className="crm-card crm-report-chart-card">
					<div className="crm-card-head">
						<div>
							<h3>Oylik kassa dinamikasi</h3>
							<p>Filtr bo'yicha qabul qilingan summa</p>
						</div>
						<span className="crm-badge green">{formatUzs(report.averageCheck)} o'rtacha chek</span>
					</div>
					<div className="crm-cash-chart-body">
						<CashLineChart
							data={report.monthly}
							onPointClick={(label) => {
								const labels = [
									"Yan",
									"Fev",
									"Mar",
									"Apr",
									"May",
									"Iyun",
									"Iyul",
									"Avg",
									"Sen",
									"Okt",
									"Noy",
									"Dek",
								];
								const monthIndex = labels.indexOf(label);
								if (monthIndex !== -1) {
									const start = dayjs().month(monthIndex).startOf("month").format("YYYY-MM-DD");
									const end = dayjs().month(monthIndex).endOf("month").format("YYYY-MM-DD");
									setDateRange([start, end]);
								}
							}}
						/>
					</div>
				</div>

				<div className="crm-card crm-report-chart-card">
					<div className="crm-card-head">
						<div>
							<h3>Usul bo'yicha</h3>
							<p>Naqd, bank va karta ulushi</p>
						</div>
						<span className="crm-badge violet">-{formatUzs(report.totalDiscount)} skidka</span>
					</div>
					<div className="crm-cash-chart-body">
						<MethodDonut
							items={report.byMethod}
							total={report.total}
							onSegmentClick={(m) => setMethod(method === m ? "all" : m)}
						/>
					</div>
				</div>
			</section>

			<section className="crm-cash-bottom-grid">
				<div className="crm-card crm-table-card crm-cash-table-card">
					<div className="crm-card-head">
						<div>
							<h3>Oxirgi kassa operatsiyalari</h3>
						</div>
						<div className="crm-cash-table-actions">
							<Input
								className="crm-cash-search"
								prefix={<SearchOutlined />}
								placeholder="Mijoz, xona, chek..."
								value={searchQuery}
								onChange={(event) => setSearchQuery(event.target.value)}
							/>
							<Button
								icon={<ReloadOutlined spin={isRefreshing} />}
								onClick={() => {
									setIsRefreshing(true);
									setTimeout(() => setIsRefreshing(false), 800);
								}}
							>
								Yangilash
							</Button>
						</div>
					</div>
					<Table<PaymentRecord>
						columns={columns}
						dataSource={sortedPayments}
						rowKey="id"
						pagination={false}
						showSorterTooltip={false}
						onRow={(record) => ({
							onClick: () =>
								navigate(`/sales/${record.saleId}`, {
									state: { returnTo: location.pathname },
								}),
							style: { cursor: "pointer" },
						})}
						scroll={{ x: 900 }}
					/>
					<div
						className="crm-table-foot"
						style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
					>
						<Pagination
							current={page}
							pageSize={pageSize}
							total={totalPayments}
							showSizeChanger={false}
							showTotal={(total) => `Jami: ${total} ta`}
							onChange={(p) => setPage(p)}
							size="small"
						/>
					</div>
				</div>
			</section>
		</div>
	);
}

export default PaymentReportPage;
